<template>
  <div
    style="height: inherit"
    class="custom-chat display-none"
  >
    <validation-observer
      ref="cancelServiceForm"
      #default="{ invalid }"
    >
      <n-form-confirmation
        ref="formConfirmation"
        key="cancelServiceForm"
        :message="
          $t('general.serviceUpdateStatusTo', {
            status: $t(`status.cancel`)
          }) +
            ' ' +
            (data.duration
              ? $tc('general.forMinute', data.duration, { minute: data.duration })
              : '')
        "
        :confirm-button-text="`button.confirm`"
        :cancel-button-text="`button.no`"
        :form="$refs.cancelServiceForm"
        :disabled="invalid"
        @submit="onUpdateCancelService"
      />
    </validation-observer>

    <div
      class="body-content-overlay"
      :class="{
        show: shallShowActiveChatContactSidebar || mqShallShowLeftSidebar
      }"
      @click="
        mqShallShowLeftSidebar = shallShowActiveChatContactSidebar = false
      "
    />

    <button
      v-b-tooltip.hover
      variant="primary"
      class="btn-action detail back-icon p-0 mb-2"
      size="sm"
      @click="appStore.setServiceContentVisible(false)"
    >
      <feather-icon
        icon="ChevronLeftIcon"
        class="cursor-pointer"
        size="20"
        @click="mqShallShowLeftSidebar = false"
      />
      {{ $t('button.detail') }}
    </button>

    <section
      v-if="Object.keys(activeChannel || {}).length"
      class="chat-app-window"
    >
      <div
        class="active-chat"
        @keydown="keyCloseConversation"
      >
        <div class="chat-navbar">
          <header class="chat-header">
            <div class="d-flex align-items-center">
              <div class="sidebar-toggle d-block d-lg-none mr-1">
                <feather-icon
                  icon="MenuIcon"
                  class="cursor-pointer"
                  size="21"
                  @click="mqShallShowLeftSidebar = true"
                />
              </div>

              <b-avatar
                size="36"
                class="mr-1 cursor-pointer badge-minimal"
                variant="light-primary"
              >
                <WaterDeliveryIcon
                  v-if="
                    activeChannel.serviceType &&
                      activeChannel.serviceType.code === 'WATER_DELIVERY'
                  "
                  class="service-icon"
                />
                <LaundryIcon
                  v-else-if="
                    activeChannel.serviceType &&
                      activeChannel.serviceType.code === 'LAUNDRY'
                  "
                  class="service-icon-2"
                />
                <CleaningIcon
                  v-else-if="
                    activeChannel.serviceType &&
                      activeChannel.serviceType.code === 'CLEANING'
                  "
                  class="service-icon-3"
                />
                <feather-icon
                  v-else
                  icon="CastIcon"
                  size="22"
                />
              </b-avatar>
              <h6 class="mb-0">
                {{ serviceName }}
              </h6>
              <span
                class="ml-1"
                style="font-weight: 500"
                :class="activeChannel.status"
              >
                {{ $t(`status.${activeChannel.status}`) }}</span>
              <div class="action-btn-group">
                <b-button
                  v-if="
                    $can('manage', 'reply-customer') &&
                      activeChannel.origin !== 'external'
                  "
                  size="sm"
                  variant="outline-primary"
                  style="display: flex; align-items: center; gap: 4px"
                  @click="openChatPage"
                >
                  <feather-icon
                    icon="MessageSquareIcon"
                    size="16"
                  />
                  {{ $t('field.chat') }}
                </b-button>
                <b-button
                  v-if="
                    $can('manage', 'manage-service-price') &&
                      activeChannel.status === 'pendingPrice' &&
                      (!activeChannel.assignedOperatorId ||
                        (activeChannel.assignedOperatorId &&
                          activeChannel.isAllowToAcceptReject)) &&
                          activeChannel.allowResetPrice
                  "
                  size="sm"
                  variant="outline-info"
                  style="display: flex; align-items: center; gap: 4px"
                  @click="$refs.pendingPriceModal.show()"
                >
                  <feather-icon
                    icon="TagIcon"
                    size="16"
                  />
                  {{ $t('field.price') }}
                </b-button>
              </div>
            </div>

            <div
              class="d-flex align-items-center"
              style="position: relative"
            >
              <div
                class="btn-group-actions"
              >
                <template
                  v-if="
                    isHelper &&
                      activeChannel.status !== 'resolved' &&
                      activeChannel.status !== 'cancel' &&
                      $can('manage', 'manage-service-operation')
                  "
                >
                  <!-- Pending Status Actions -->
                  <template
                    v-if="
                      activeChannel.helperStatus === 'pending'
                    "
                  >
                    <b-button
                      size="sm"
                      class="btn-reject btn"
                      @click="handleUpdateServiceStatus('rejected')"
                    >
                      <feather-icon
                        icon="XCircleIcon"
                        size="16"
                      />
                      {{ $t('button.reject') }}
                    </b-button>
                    <b-button
                      size="sm"
                      class="btn-accept btn"
                      @click="handleUpdateServiceStatus('accepted')"
                    >
                      <feather-icon
                        icon="CheckCircleIcon"
                        size="16"
                      />
                      {{ $t('button.accept') }}
                    </b-button>
                  </template>

                  <!-- Accepted Status Actions -->
                  <template v-else-if="activeChannel.helperStatus === 'accepted'">
                    <b-button
                      size="sm"
                      class="btn"
                      variant="outline-warning"
                      @click="handleUpdateServiceStatus('processing')"
                    >
                      {{ $t('button.process') }}
                    </b-button>
                  </template>

                  <!-- Processing Status Actions -->
                  <template v-else-if="activeChannel.helperStatus === 'processing'">
                    <b-button
                      size="sm"
                      class="btn"
                      variant="outline-info"
                      @click="onUpdateCompleteStatus"
                    >
                      {{ $t('button.done') }}
                    </b-button>
                  </template>
                </template>
                <template
                  v-if="
                    activeChannel.status !== 'resolved' &&
                      activeChannel.status !== 'cancel' &&
                      $can('manage', 'manage-service-operation')
                  "
                >
                  <!-- Pending Status Actions -->
                  <template
                    v-if="
                      activeChannel.status === 'pending'
                    "
                  >
                    <b-button
                      v-if="activeChannel.isAllowToAcceptReject"
                      size="sm"
                      class="btn-reject btn"
                      @click="handleUpdateServiceStatus('rejected')"
                    >
                      <feather-icon
                        icon="XCircleIcon"
                        size="16"
                      />
                      {{ $t('button.reject') }}
                    </b-button>
                    <b-button
                      v-if="activeChannel.isAllowToAcceptReject"
                      size="sm"
                      class="btn-accept btn"
                      @click="handleUpdateServiceStatus('accepted')"
                    >
                      <feather-icon
                        icon="CheckCircleIcon"
                        size="16"
                      />
                      {{ $t('button.accept') }}
                    </b-button>
                    <b-button
                      size="sm"
                      class="btn"
                      variant="outline-danger"
                      @click="handleCancelService"
                    >
                      {{ $t('button.cancel') }}
                    </b-button>
                  </template>

                  <!-- Accepted Status Actions -->
                  <template v-else-if="activeChannel.status === 'accepted'">
                    <b-button
                      size="sm"
                      class="btn"
                      variant="outline-danger"
                      @click="handleCancelService"
                    >
                      {{ $t('button.cancel') }}
                    </b-button>
                    <b-button
                      v-if="activeChannel.isAllowToAcceptReject"
                      size="sm"
                      class="btn"
                      variant="outline-warning"
                      @click="handleUpdateServiceStatus('processing')"
                    >
                      {{ $t('button.process') }}
                    </b-button>
                  </template>

                  <!-- Processing Status Actions -->
                  <template v-else-if="activeChannel.status === 'processing' && !activeChannel.isMainOperatorCompleted">
                    <b-button
                      size="sm"
                      class="btn"
                      variant="outline-danger"
                      @click="handleCancelService"
                    >
                      {{ $t('button.cancel') }}
                    </b-button>
                    <b-button
                      v-if="activeChannel.isAllowToAcceptReject"
                      size="sm"
                      class="btn"
                      variant="outline-warning"
                      @click="handleChangeHoldStatus"
                    >
                      {{ $t('button.hold') }}
                    </b-button>
                    <b-button
                      v-if="activeChannel.isAllowToAcceptReject"
                      size="sm"
                      class="btn"
                      variant="outline-info"
                      @click="onUpdateCompleteStatus"
                    >
                      {{ $t('button.done') }}
                    </b-button>
                  </template>

                  <!-- Hold Status Actions -->
                  <template v-else-if="activeChannel.status === 'hold'">
                    <b-button
                      size="sm"
                      class="btn"
                      variant="outline-danger"
                      @click="handleCancelService"
                    >
                      {{ $t('button.cancel') }}
                    </b-button>
                    <b-button
                      v-if="activeChannel.isAllowToAcceptReject"
                      size="sm"
                      class="btn"
                      variant="outline-warning"
                      @click="handleUpdateServiceStatus('processing')"
                    >
                      {{ $t('button.process') }}
                    </b-button>
                  </template>

                  <!-- PendingPrice Status Actions -->
                  <template
                    v-else-if="activeChannel.status === 'pendingPrice' ||
                      activeChannel.status === 'completed' ||
                      activeChannel.status === 'pendingDispatch'"
                  >
                    <b-button
                      size="sm"
                      class="btn"
                      variant="outline-danger"
                      @click="handleCancelService"
                    >
                      {{ $t('button.cancel') }}
                    </b-button>
                  </template>
                </template>
                <b-button
                  v-if="
                    activeChannel.status === 'processing' &&
                      activeChannel.isMainOperatorCompleted &&
                      $can('manage', 'manage-service-operation')
                  "
                  size="sm"
                  class="btn"
                  variant="outline-danger"
                  @click="handleCancelService"
                >
                  {{ $t('button.cancel') }}
                </b-button>
                <button
                  v-if="
                    $can('manage', 'manage-modify-service') &&
                      activeChannel.status !== 'resolved' &&
                      activeChannel.status !== 'cancel' &&
                      activeChannel.status === 'completed'
                  "
                  :style="{
                    color: 'rgb(151, 184, 52)',
                    border: '1px solid rgb(151, 184, 52) !important'
                  }"
                  variant="outline-info"
                  class="btn btn-sm"
                  size="sm"
                  @click="handleOpenStatus('reset', 'resetPrice')"
                >
                  {{ $t('field.reset_price') }}
                  <div
                    v-if="activeChannel.status === 'pendingDispatch'"
                    class="notification-dot"
                  />
                </button>
                <button
                  v-if="
                    $can('manage', 'manage-modify-service') &&
                      activeChannel.status !== 'resolved' &&
                      activeChannel.status !== 'cancel' &&
                      (activeChannel.status === 'pending' ||
                        (activeChannel.status === 'pendingPrice' &&
                          activeChannel.assignedOperatorId))
                  "
                  :style="{
                    color: 'rgb(151, 184, 52)',
                    border: '1px solid rgb(151, 184, 52) !important'
                  }"
                  variant="outline-info"
                  size="sm"
                  class="btn btn-sm"
                  @click="
                    handleOpenStatus(
                      activeChannel.status === 'pending' ? 'restart' : 'reset',
                      activeChannel.status === 'pending'
                        ? 'reassignOperator'
                        : 'reassignPriceSpecialist'
                    )
                  "
                >
                  {{ $t('button.reassign') }}
                  <div
                    v-if="activeChannel.status === 'pendingDispatch'"
                    class="notification-dot"
                  />
                </button>
                <button
                  v-if="
                    $can('manage', 'manage-modify-service') &&
                      activeChannel.status !== 'resolved' &&
                      activeChannel.status !== 'cancel' &&
                      (activeChannel.status === 'pendingDispatch' ||
                        (activeChannel.status === 'pendingPrice' &&
                          !activeChannel.assignedOperatorId))
                  "
                  :style="{
                    color: 'rgb(151, 184, 52)',
                    border: '1px solid rgb(151, 184, 52) !important'
                  }"
                  variant="outline-info"
                  size="sm"
                  class="btn btn-sm"
                  @click="
                    handleOpenStatus(
                      activeChannel.status === 'pendingDispatch'
                        ? 'restart'
                        : 'reset',
                      activeChannel.status === 'pendingDispatch'
                        ? 'assignOperator'
                        : 'assignPriceSpecialist'
                    )
                  "
                >
                  {{ $t('button.assign') }}
                  <div
                    v-if="activeChannel.status === 'pendingDispatch'"
                    class="notification-dot"
                  />
                </button>
                <button
                  v-if="
                    $can('manage', 'manage-modify-service') &&
                      activeChannel.status !== 'resolved' &&
                      activeChannel.status !== 'cancel' &&
                      (activeChannel.status === 'accepted' ||
                        activeChannel.status === 'processing' ||
                        activeChannel.status === 'completed' ||
                        activeChannel.status === 'hold' ||
                        activeChannel.status === 'pendingPrice')
                  "
                  :style="{
                    color: 'rgb(80, 184, 57) !important',
                    border: '1px solid rgb(80, 184, 57) !important'
                  }"
                  variant="outline-info"
                  size="sm"
                  class="btn btn-sm"
                  @click="handleOpenStatus('restart', 'restartService')"
                >
                  {{ $t('field.restart_service') }}
                  <div
                    v-if="activeChannel.status === 'pendingDispatch'"
                    class="notification-dot"
                  />
                </button>
                <b-button
                  v-if="
                    $can('manage', 'manage-modify-service') &&
                      activeChannel.status !== 'resolved' &&
                      activeChannel.status !== 'cancel' && (
                        activeChannel.status === 'pending'
                        || activeChannel.status === 'accepted'
                        || activeChannel.status === 'processing')
                  "
                  size="sm"
                  class="btn"
                  style="border: var(--primary) 1px solid !important; color: var(--primary) !important"
                  variant="outline-danger"
                  @click="() => handleAddOthers()"
                >
                  {{ $t('button.add_others') }}
                </b-button>
              </div>

            </div>
          </header>

          <div
            v-if="
              isTranslate() && activeChannel.operatorAssigned == userData.id
            "
          >
            <div
              v-if="!activeChannel.isAutoTranslate && detectedLanguage"
              class="translateBox bg-primary"
            >
              <div
                class="dropdown chat-filter-dropdown ml-1"
                style="display: flex"
              >
                <div style="padding-top: 4px">
                  {{
                    $t('chat.detectedLanguage', {
                      visitorName: activeChannel.customer.name
                    })
                  }}
                </div>
                &nbsp;
                <b-dropdown
                  variant="link"
                  no-caret
                  toggle-class="p-0"
                  class="detectLanguage-dropdown"
                >
                  <template #button-content>
                    <flag
                      :iso="getCountryCode(detectedLanguage.language)"
                      :squared="false"
                    />&nbsp;
                    <span class="text-white">{{ detectedLanguage.name }}</span>
                  </template>

                  <b-dropdown-form class="sticky-search">
                    <b-form-group>
                      <b-form-input
                        v-model="textSearch"
                        :placeholder="$t('general.search')"
                        @keydown.enter.prevent
                        @input="searchLocal"
                      />
                    </b-form-group>
                  </b-dropdown-form>

                  <b-dropdown-item
                    v-for="(local, index) in currentLocale"
                    v-if="localFilterSearch == ''"
                    :key="index"
                    @click="changeDetectedLanguage(local)"
                  >
                    <template v-if="local">
                      <flag
                        :style="{ zIndex: -1 }"
                        :iso="getCountryCode(local.language)"
                        :squared="false"
                      />
                      &nbsp;&nbsp;{{ local.name }}
                    </template>
                  </b-dropdown-item>

                  <b-dropdown-item
                    v-for="(local, index) in filteredLocal"
                    v-if="localFilterSearch != ''"
                    :key="index"
                    @click="changeDetectedLanguage(local)"
                  >
                    <template v-if="local">
                      <flag
                        :iso="getCountryCode(local.language)"
                        :squared="false"
                      />
                      &nbsp;&nbsp;{{ local.name }}
                    </template>
                  </b-dropdown-item>
                </b-dropdown>

                &nbsp;
                <b-button
                  class="cursor-pointer btn btn-success btn-sm"
                  :disabled="loading"
                  @click="
                    activeChannel.state == conversationStatus.open
                      ? isAutoTranslate(true)
                      : showAlert()
                  "
                >
                  <span v-if="!loading">{{
                    $t('chat.startLiveTranslate')
                  }}</span>
                  <span v-else>{{ $t('general.loading') }}</span>
                </b-button>
              </div>
            </div>
            <div
              v-if="activeChannel.isAutoTranslate"
              class="bg-primary translateBox"
            >
              <div v-if="operatorLanguage">
                <span
                  v-b-tooltip.hover
                  :title="operatorLanguage.name"
                  style="cursor: pointer"
                >
                  <flag
                    :iso="getCountryCode(operatorLanguage.language)"
                    :squared="false"
                  />&nbsp;
                </span>
                &nbsp;
                <span
                  v-b-tooltip.hover
                  :title="detectedLanguage.name"
                  style="cursor: pointer"
                >
                  <flag
                    :iso="getCountryCode(detectedLanguage.language)"
                    :squared="false"
                  />&nbsp;
                </span>
                {{ $t('chat.liveTranslate') }}&nbsp;
                <b-button
                  class="cursor-pointer btn btn-danger btn-sm"
                  :disabled="loading"
                  @click="
                    activeChannel.state == conversationStatus.close
                      ? showAlert()
                      : isAutoTranslate(false)
                  "
                >
                  <span v-if="!loading">{{ $t('general.stop') }}</span>
                  <span v-else>{{ $t('general.loading') }}</span>
                </b-button>
              </div>
            </div>
          </div>
        </div>

        <div :class="messageBodyClass">
          <div
            ref="refChatLogPS"
            class="user-chats scroll-area"
            :class="{
              'pt-2': !connectionStatus
            }"
            style="height: calc(100% - 40px) !important"
            @click="onBodyClick"
            @scroll="handleScrollMessage"
          >
            <div class="infinite-wrapper" />
            <chat-message
              :key="chatBoxKey"
              :active-tab="activeTab"
              :nested-active-tab="nestedActiveTab"
              :active-channel="activeChannel"
              :messages="messages"
              :read-message="activeChannel.readMessage"
              :is-typing="isTyping"
              :helper-list="helperList"
              @update-helper-list="helperList = $event"
              @retry="retrySendMessage"
              @translate="manualTranslate"
              @change-active-tab="activeTab = $event"
              @change-nested-active-tab="nestedActiveTab = $event"
              @edit-operator="handleOpenOperator"
              @edit-status="(title) => handleOpenStatus('restart', title)"
              @filter="listChannel"
              @success="handleAddOthersSuccess"
            />
          </div>
          <div
            v-if="!hidePullDown && messages.length && !isSendingMsg"
            class="action-btn pull-down-btn btn-primary rounded-circle"
            :style="{
              bottom: pullDownStyle
            }"
            @click="fetchLatest"
          >
            <feather-icon
              icon="ArrowDownCircleIcon"
              size="40"
            />
          </div>
        </div>
      </div>
    </section>

    <portal to="content-renderer-sidebar-left">
      <chat-left-sidebar
        :identifier="identifier"
        :channels="channels"
        :active-channel="activeChannel"
        :mq-shall-show-left-sidebar.sync="mqShallShowLeftSidebar"
        :connection-status="connectionStatus"
        :active-tab="activeTab"
        :total="total"
        :loading="loading"
        @filter="filter"
        @open-chat="openChannel"
        @load-channel="listChannel"
        @closeChannel="closeChannel"
        @assign="handleAssign"
        @price="handlePrice"
        @cancel="handleCancel"
        @addOther="handleAddOthers"
        @goToChat="goToChatPage"
      />
    </portal>

    <assign-operator-modal
      ref="assignOperatorChatModal"
      @success="onAssignSuccess"
    />
    <update-status-modal
      ref="updateStatusChatModal"
      :is-helper="isHelper"
      :active-channel="activeChannel"
      :update-complete-status-visible="updateCompleteStatusVisible"
      @success="onUpdateStatusSuccess"
      @hide="hideCompleteStatusModal"
      @update-complete-status="onUpdateCompleteStatus"
      @update-pending-price="onUpdatePendingPrice"
      @show-pending-price="$refs.pendingPriceModal.show()"
      @loading="completeLoading = $event"
    />
    <update-complete-status-modal
      ref="updateCompleteStatusRef"
      :loading="completeLoading"
      :active-channel="activeChannel"
      @cancel="onCloseHoldModal"
      @update="onUpdateCompleteStatusModal"
    />
    <load-profile />
    <update-complete-status-standalone-modal
      ref="updateCompleteStatusRefStandalone"
      :active-channel="activeChannel"
    />
    <update-hold-status-standalone-modal ref="updateHoldStatusRefStandalone" />

    <hold-duration-modal
      ref="holdDurationRef"
      :loading="loadingHold"
      @update="onUpdateHoldModal"
    />

    <pending-price-modal
      ref="pendingPriceModal"
      :active-channel="activeChannel"
      @success="handlePendingPriceSuccess"
      @show-complete-modal="handleShowCompleteModal"
    />
    <cancel-service-modal
      ref="cancelServiceModal"
      :loading="cancelServiceLoading"
      :shown="showCancelServiceModal"
      @cancel="onCloseCancelServiceModal"
      @update="handleConfirmCancelService"
    />
    <add-others-modal
      ref="addOthersModal"
      :must-excluded-ids="excludedIds"
      @success="handleAddOthersSuccess"
    />
  </div>
</template>

<script>
import store from '@/store'
import { ref } from '@vue/composition-api'
import {
  BAvatar,
  BDropdown,
  BDropdownItem,
  BForm,
  BInputGroup,
  BFormInput,
  BButton,
  BBadge,
  BSpinner,
  BImg,
  BDropdownDivider,
  BDropdownForm,
  BFormGroup,
  BInputGroupPrepend,
} from 'bootstrap-vue'
import NFormConfirmation from '@/components/NFormConfirmation.vue'
import { ValidationObserver } from 'vee-validate'
import ToastificationAvatar from '@core/components/toastification/ToastificationAvatar.vue'

import { $themeBreakpoints } from '@themeConfig'
import { useResponsiveAppSidebarVisibility } from '@core/comp-functions/ui/app'
import Repository from '@/repositories/RepositoryFactory'
import conversationStatus from '@/data/serviceSystemStatus'
import messageType from '@/data/messageType'
import messageStatus from '@/data/messageStatus'
import languageEn from '@/data/languages/en'
import languageKm from '@/data/languages/km'
import languageZh from '@/data/languages/zh-cn'
import languageVi from '@/data/languages/vi'
import { getCountryByLanguageCode } from '@/libs/helper'
import { v4 as uuidv4 } from 'uuid'
import moment from 'moment'
import InfiniteLoading from 'vue-infinite-loading'
import { connectSocket } from '@/libs/socket'
import Client from '@/repositories/Clients/AxiosClient'
import Config from '@/repositories/Clients/AxiosDefaultConfig'
import { e2eDecrypt } from '@/libs/crypto-js'
import VueSweetalert2 from 'vue-sweetalert2'
import localeCountries from '@/data/localeCountries'
import useJwt from '@/auth/jwt/useJwt'
import NSearchContainer from '@/components/NSearchContainer'
import NSearchInput from '@/components/NSearchInput'
import serviceSidebar from '@/navigation/vertical/menu/service'
import useAppStore from '@/store-pinia/useAppStore'
import WaterDeliveryIcon from '@/components/icons/WaterDeliveryIcon.vue'
import LaundryIcon from '@/components/icons/LaundryIcon.vue'
import CleaningIcon from '@/components/icons/CleaningIcon.vue'
import { bus } from '@/libs/bus'
import ChatRightSidebar from './GChatRightSidebar.vue'
// import AssignOperatorModal from './AssignOperatorModal.vue'
// import AssignMerchantModal from './AssignMerchantModal.vue'
import ChatInput from './ChatInput.vue'
import ChatMessage from './ChatMessage.vue'
import ChatActiveChatContentDetailsSidedbar from './ChatActiveChatContentDetailsSidedbar.vue'
import ChatLeftSidebar from './ChatLeftSidebar.vue'
import DateInputs from './searchDate.js'
import AssignOperatorModal from './components/AssignOperatorModal.vue'
import UpdateStatusModal from './components/UpdateStatusModal.vue'
import UpdateCompleteStatusModal from './components/UpdateCompleteStatusModal.vue'
import UpdateCompleteStatusStandaloneModal from './components/UpdateCompleteStatusStandaloneModal.vue'
import UpdateHoldStatusStandaloneModal from './components/UpdateHoldStatusStandaloneModal.vue'
import PendingPriceModal from './components/PendingPriceModal.vue'
import CancelServiceModal from './components/CancelServiceModal.vue'
import HoldDurationModal from '../components/HoldDurationModal'
import AddOthersModal from './components/AddOthersModal.vue'

const ServiceSystemRepository = Repository.get('serviceSystem')
const ChatChannelRepository = Repository.get('chatChannel')
const ChatMessageRepository = Repository.get('chatMessage')
const TranslateRepository = Repository.get('translateRepository')
// const ChatAssignmentRepository = Repository.get('chatAssignmentRepository')
const PartnersShipRepository = Repository.get('partnersShip')

export default {
  components: {
    // BSV
    BAvatar,
    BDropdown,
    BDropdownItem,
    BForm,
    BInputGroup,
    BFormInput,
    BButton,
    BBadge,
    BSpinner,
    BImg,
    BDropdownDivider,
    BDropdownForm,
    BFormGroup,
    BInputGroupPrepend,

    // 3rd Party
    InfiniteLoading,
    VueSweetalert2,

    // SFC
    ChatLeftSidebar,
    ChatRightSidebar,
    ChatActiveChatContentDetailsSidedbar,
    ChatMessage,
    ChatInput,
    // AssignOperatorModal,
    // AssignMerchantModal,
    NSearchContainer,
    NSearchInput,
    AssignOperatorModal,
    UpdateStatusModal,
    UpdateCompleteStatusModal,
    UpdateCompleteStatusStandaloneModal,
    UpdateHoldStatusStandaloneModal,
    PendingPriceModal,
    CancelServiceModal,
    NFormConfirmation,
    ValidationObserver,
    WaterDeliveryIcon,
    LaundryIcon,
    CleaningIcon,
    HoldDurationModal,
    AddOthersModal,
  },
  data() {
    return {
      channelFilter: {
        state: null,
        search: null,
        operator: null,
        isAuto: null,
        isUnread: null,
        isBlock: null,
        searchType: 'm',
        startDate: null,
        endDate: null,
        status: null,
        departmentId: null,
        serviceSystemId: null,
        priority: null,
      },
      activeTab: 0,
      nestedActiveTab: 0,
      updateCompleteStatusVisible: false,
      localFilterSearch: '',
      identifier: 1,
      activeChannel: {},
      channels: [],
      total: 0,
      channel: '',
      messages: [],
      message: '',
      channelPerPage: 15,
      channelPage: 1,
      messageLimit: 15,
      socket: null,
      isShowEmojiPicker: false,
      shallShowActiveChatContactSidebar: false,
      connectionStatus: true,
      isTyping: false,
      randomKey: null,
      isAutoAssignOperator: false,
      completeLoading: false,
      cancelServiceLoading: false,
      lastSeenMessageId: null,
      lastSeenMessageAt: null,

      conversationStatus,
      messageType,
      messageStatus,
      isModalOpen: false,
      show: false,
      // isTranslationOn: null,
      chatBoxKey: 0,
      textSearch: '',
      loading: false,
      hidePullDown: true,
      debounce: this.createDebounce(),
      dialogVisible: false,
      query: {
        startDate: '',
        endDate: '',
      },
      isDateTimeFilter: false,
      filterOffset: 0,
      footerHeight: localStorage.getItem('chatFooterHeight')
        ? parseInt(localStorage.getItem('chatFooterHeight'))
        : 65,
      isSendingMsg: false,
      serviceSidebar,
      pendingPriceModalVisible: false,
      data: {
        duration: 0,
        remark: '',
      },
      showCancelServiceModal: false,
      loadingHold: false,
      helperList: [],
      excludedIds: [],
      selectedServiceId: null,
    }
  },
  computed: {
    // mustExcludedIds() {
    //   return [this.activeChannel.assignedOperatorId, ...(this.activeChannel.helperOperatorIds || [])].filter(Boolean)
    // },
    isHelper() {
      return (this.activeChannel?.helperOperatorIds || []).includes(this.userData.id)
    },
    serviceName() {
      const { name, shortenName } = this.activeChannel || {}
      if (name) {
        return (
            name?.[this.$i18n.locale.split('-')[0]]
            || name?.en
            || name?.zh
            || name?.km
            || name?.vi
            || '-'
        )
      }
      return shortenName
    },
    userData() {
      return this.$store.state.profile.email
          || this.$store.state.profile.phone
        ? this.$store.state.profile
        : JSON.parse(
          localStorage.getItem(useJwt.jwtConfig.storageUserDataKeyName),
        )
    },
    isTransfer() {
      if (this.activeChannel.transferred) {
        const transferring = this.activeChannel.transferred.find(
          e => !e.endedAt,
        )
        if (transferring) {
          return true
        }
      }
      return false
    },
    trasferredData() {
      if (this.activeChannel.transferred) {
        return this.activeChannel.transferred.find(e => !e.endedAt)
      }
      return {}
    },
    profilePicture() {
      if (this.$store.state.profile.profilePicture) {
        return this.$store.state.profile.profilePicture
      }

      return this.userData.profilePicture
    },
    detectedLanguage() {
      const data = this.getCountryByLanguageCode(
        this.currentLocale,
        this.activeChannel.detectedLanguage || this.$i18n.locale,
      )
      if (data) {
        return data
      }
      return null
    },
    operatorLanguage() {
      return getCountryByLanguageCode(
        this.currentLocale,
        this.activeChannel.operatorLanguage,
      )
    },
    currentLocale() {
      if (this.$i18n.locale === 'km') {
        return this.languageKm
      }
      if (this.$i18n.locale === 'en') {
        return this.languageEn
      }
      if (this.$i18n.locale === 'vi') {
        return this.languageVi
      }

      return this.languageZh
    },
    filteredLocal() {
      let local = []
      if (this.$i18n.locale === 'km') {
        local = this.languageKm
      } else if (this.$i18n.locale === 'en') {
        local = this.languageEn
      } else if (this.$i18n.locale === 'vi') {
        local = this.languageVi
      } else {
        local = this.languageZh
      }
      if (this.localFilterSearch.length > 0) {
        local = local.filter(value => {
          const name = value?.name?.toLowerCase()
          return name.startsWith(this.localFilterSearch.toLowerCase())
        })
      }
      return local
    },
    isSearchingMessage() {
      return (
        this.channelFilter.searchType == 'm'
          && this.channelFilter.search?.trim()
      )
    },
    messageBodyClass() {
      if (
        !this.isTransfer
          && this.activeChannel.state == this.conversationStatus.open
          && (!this.activeChannel.operatorAssigned
            || this.activeChannel.operatorAssigned == this.userData.id)
          && !this.activeChannel.blockAt
      ) {
        this.resizeChatBody()
        return 'messages-body'
      }
      this.resetChatBodyResize()
      return 'message-body-no-chat-input'
    },
    isShowChatInput() {
      return (
        !this.isTransfer
          && this.activeChannel.state == this.conversationStatus.open
          && (!this.activeChannel.operatorAssigned
            || this.activeChannel.operatorAssigned == this.userData.id)
          && !this.activeChannel.blockAt
      )
    },
    pullDownStyle() {
      if (!this.isShowChatInput) {
        return '75px'
      }
      if (this.isTranslate() && this.footerHeight > 65) {
        return `${this.footerHeight + 30}px`
      }
      if (!this.isTranslate() && this.footerHeight >= 65) {
        return `${this.footerHeight + 25}px`
      }
      return '130px'
    },
  },
  watch: {
    'appStore.serviceCategories': {
      handler(newValue, oldValue) {
        try {
          if (
            JSON.stringify(newValue || {}) !== JSON.stringify(oldValue || {})
          ) {
            this.listChannel()
          }
        } catch (error) {
          // handling error
        }
      },
      deep: true,
      immediate: true,
    },
    lastSeenMessageId(value, oldValue) {
      if (value && value != oldValue) {
        this.submitReadMessage(
            this.activeChannel?.token,
            value,
            this.lastSeenMessageAt,
        )
      }
    },
    activeChannel(value, oldValue) {
      this.excludedIds = [value?.assignedOperatorId, ...(value?.helperOperatorIds || [])].filter(Boolean)
      if (value && value !== oldValue) {
        this.dialogVisible = false
        this.isDateTimeFilter = false
        this.filterOffset = 0
        if (
          !this.$route.query.channelId
            && !this.$route.query.from === 'topic-summary'
        ) {
          this.query = {
            startDate: '',
            endDate: '',
          }
        }
        this.$nextTick(() => {
          this.resizeChatBody()
        })
      }
    },
  },
  mounted() {
    this.$root.$on('bv::modal::shown', (bvEvent, modalId) => {
      bvEvent.vueTarget.$refs['close-button'].setAttribute('tabIndex', '-1')
        bvEvent.vueTarget.$refs['ok-button']?.focus()
    })
    window.addEventListener('keydown', this.keyCloseConversation)

    this.textSearch = this.localFilterSearch
  },
  created() {
    this.connectSocket()
    this.randomKey = uuidv4()
    if (localStorage.getItem('tabKey') == null) {
      localStorage.setItem('tabKey', this.randomKey)
    }

    const self = this
    window.addEventListener('beforeunload', () => {
      localStorage.removeItem('tabKey')
    })
    window.addEventListener('storage', evt => {
      if (evt.key == 'tabKey' && evt.newValue == null) {
        if (localStorage.getItem('tabKey') == null) {
          localStorage.setItem('tabKey', self.randomKey)
        }
      }
    })

    bus.$on('notificationClick', type => {
      if (type == 'service') {
        this.$nextTick(() => {
          this.listChannel()
        })
      }
    })
  },
  destroyed() {
    bus.$off('notificationClick')
    window.removeEventListener('keydown', this.keyCloseConversation)
    this.activeChannel = {}
  },
  methods: {
    goToChatPage(serviceDocument) {
      this.loading = true
      const serviceObject = serviceDocument || this.activeChannel;
      if (!serviceObject) return;

      const payload = {
        serviceSystemId: serviceObject.id,
        clientLocale: this.$i18n.locale
      };
      ServiceSystemRepository.getChatChannel(payload)
        .then(response => {
          const data = response.data.data;
          const url = this.$router.resolve({
            name: 'chat',
            query: {
              status: null,
              searchType: 'c',
              search: data.name,
              from: 'service-system',
              channelId: data.id,
              cache: 'no',
            }
          });
          window.open(url.href, '_blank');
        })
        .catch(error => {
          console.log('error>>>>', error);
        })
        .finally(() => {
          this.loading = false
        });
    },
    handleAddOthers(e) {
      const op = e || this.activeChannel
      this.excludedIds = [op?.assignedOperatorId, ...(op?.helperOperatorIds || [])].filter(Boolean)
      this.selectedServiceId = op.id
      this.$refs.addOthersModal.show(op)
    },
    handleAddOthersSuccess() {
      if (this.activeChannel.id === this.selectedServiceId) return

      ServiceSystemRepository.show(this.selectedServiceId).then(response => {
        const index = this.channels.findIndex(c => c.id === this.selectedServiceId)
        if (index !== -1) {
          this.channels.splice(index, 1, response.data.data)
        }
      })
    },
    handleCancel(data) {
      this.activeChannel = data
      this.handleCancelService()
    },
    handleAssign(data, type, title) {
      this.activeChannel = data
      this.handleOpenStatus(type, title)
    },
    handlePrice(data) {
      this.activeChannel = data
      this.onUpdatePendingPrice()
    },
    async onUpdateHoldModal(duration, remark) {
      this.data.duration = duration
      this.data.remark = remark
      this.loadingHold = true
      try {
        await ServiceSystemRepository.updateStatus(
          {
            status: 'hold',
            ...(this.data.duration && {
              noOfminuteHold: +this.data.duration,
            }),
            ...(this.data.remark && { remark: this.data.remark.trim() }),
          },
          this.activeChannel.id,
        )
        this.$toast({
          component: ToastificationAvatar,
          position: 'top-right',
          timeout: 10000,
          props: {
            title: this.$t('alert.service'),
            icon: 'CastIcon',
            variant: 'success',
            text: this.$t('alert.serviceModifySuccessfully'),
          },
        })
        this.data.remark = ''
        this.data.duration = 0
        this.$refs.holdDurationRef.hide()
      } catch (error) {
        if (error.response?.status === 422) {
          this.$refs.assignOperatorForm.setErrors(
              error.response?.data?.validate,
          )
        }
      } finally {
        this.loadingHold = false
      }
    },
    handleUpdateServiceStatus(status = 'rejected') {
      this.$bvModal
        .msgBoxConfirm(
          this.$t('general.serviceUpdateStatusTo', {
            status: this.$t(
              status === 'pendingPrice' ? 'button.done' : `status.${status}`,
            ),
          }),
          {
            title: this.$t('general.updateServiceSystem'),
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'primary',
            okTitle: this.$t('button.confirm'),
            cancelTitle: this.$t('button.no'),
            cancelVariant: 'outline-secondary',
            hideHeaderClose: false,
            centered: true,
            loading: true,
          },
        )
        .then(async value => {
          if (value) {
            if (this.isHelper) {
              await ServiceSystemRepository.updateHelperStatus(
                { status },
                this.activeChannel.id,
              )
            } else {
              await ServiceSystemRepository.updateStatus(
                { status },
                this.activeChannel.id,
              )
            }

            this.$toast({
              component: ToastificationAvatar,
              position: 'top-right',
              timeout: 10000,
              props: {
                title: this.$t('alert.service'),
                icon: 'CastIcon',
                variant: 'success',
                text: this.$t('alert.serviceUpdatedSuccessfully', {
                  status: this.$t(
                    status === 'pendingPrice'
                      ? 'button.done'
                      : `status.${status || 'completed'}`,
                  ),
                }),
              },
            })
          }
        })
    },
    openChatPage() {
      if (
        this.activeChannel.status === 'pending'
          && this.activeChannel.isAllowToAssign
      ) {
        ChatChannelRepository.assign({
          token: this.activeChannel.chatChannelToken,
          userId: this.userData.id,
        })
          .then(response => {
            const data = response?.data?.data
            if (data) {
              const url = this.$router.resolve({
                name: 'chat',
                query: {
                  from: 'topic-summary',
                  channelId: this.activeChannel.chatChannelId,
                },
              })
              window.open(url.href, '_blank')
            }
          })
          .catch(() => {
            // handle error
          })
      } else {
        const url = this.$router.resolve({
          name: 'chat',
          query: {
            from: 'topic-summary',
            channelId: this.activeChannel.chatChannelId,
          },
        })
        window.open(url.href, '_blank')
      }
    },
    updateQuerySting() {
      this.$router.replace({ query: this.query })
      store.commit('app/UPDATE_SERVICE_SYSTEM_QUERY', { ...this.query })
    },
    getCountryCode(language) {
      return this.localeCountries.find(
        e => e.language.toLowerCase() === language.toLowerCase(),
      ).flag
    },

    isTranslate() {
      if (
        this.activeChannel.isAutoTranslate
          && this.activeChannel.operatorAssigned !== this.userData.id
      ) {
        return true
      }
      // const chatSetting = JSON.parse(localStorage.getItem('chatSettings')).isTranslationOn == 1
      const chatChannel = this.activeChannel.state != 1 // `1` = channel is opening
      const isTransferToMerchant = this.isTransfer
      const isAsignToOtherOperator = this.activeChannel.operatorAssigned != this.userData.id
      return chatChannel || isTransferToMerchant || isAsignToOtherOperator
    },
    showAlert() {
      this.$swal.fire({
        text: this.$t('alert.pleaseOpenConversationFirst'),
        icon: 'warning',
        confirmButtonText: this.$t('button.close'),
      })
    },
    isAutoTranslate(isAutoTranslate) {
      this.loading = true
      const payload = {
        token: this.activeChannel.token,
        isAutoTranslate,
        detectedLanguage:
            this.activeChannel?.detectedLanguage || this.$i18n.locale,
      }
      ChatChannelRepository.isAutoTranslate(payload)
        .then(response => {
          this.loading = false
          const data = response?.data?.data
          if (data) {
            this.activeChannel = {
              ...this.activeChannel,
              isAutoTranslate: data.isAutoTranslate,
              detectedLanguage: data.detectedLanguage,
              operatorLanguage: data.operatorLanguage,
            }
          }
        })
        .catch(err => {
          this.loading = false
        })
    },
    searchLocal(text) {
      this.localFilterSearch = text
    },
    changeDetectedLanguage(selectedLang) {
      return (this.activeChannel = {
        ...this.activeChannel,
        detectedLanguage: selectedLang.language,
      })
    },
    changePreTextMessage(text) {
      this.$refs.chatInput.text = text
        this.$refs.chatInput.$refs.inputBox?.$el?.focus()
    },
    toggleRightSidebar() {
      this.mqShallShowRightSidebar = !this.mqShallShowRightSidebar
      this.preTextMessage = ''
    },
    connectSocket() {
      this.socket = connectSocket()

      this.socket.on('connect', () => {
        this.connectionStatus = true
        console.log('connect', this.socket)
        this.listenSocket()
        this.socket.emit('join', {
          event: `${this.userData.merchantId}:services`,
        })
      })

      this.socket.on('error', error => {
        console.log('error', error)
      })

      this.socket.on('connect_error', () => {
        console.log('connect_error', this.socket)
      })

      this.socket.on('connect_timeout', () => {
        console.log('connect_timeout', this.socket)
      })

      this.socket.on('disconnect', () => {
        this.connectionStatus = false
        console.log('disconnect', this.socket)
      })

      this.socket.on('ping', () => {
        console.log('ping', this.socket)
      })

      this.socket.on('reconnect', attempt => {
        console.log('reconnect', attempt)
      })

      this.socket.on('reconnect_attempt', attempt => {
        console.log('reconnect_attempt', attempt)
      })

      this.socket.on('reconnect_error', error => {
        console.log('reconnect_error', error)
      })

      this.socket.on('reconnect_failed', () => {
        console.log('reconnect_failed', this.socket)
      })
    },
    leaveChannel(channel) {
      if (channel) {
        this.socket.emit('leave', {
          event: this.channel,
        })
      }
    },
    joinChannel(channel) {
      if (channel) {
        this.socket.emit('leave', {
          event: `${this.userData.merchantId}:${channel.id}`,
        })
      }

      this.socket.emit('join', {
        event: `${this.userData.merchantId}:${this.activeChannel.id}`,
      })
    },
    listenSocket() {
      this.socket.on('serviceId:actions', data => {
        if (data?.data) {
          try {
            data.data = JSON.parse(data.data)
          } catch (error) {
            console.warn('[serviceId:actions]: Error: ', error, data.data)
          }
        }

        // if (window.DEBUG) {
        // console.log('[serviceId:actions]: ', data)
        // }

        if (data.type === 'serviceId-helper') {
          switch (data.actionType) {
            case 'listHelper':
              {
                const existingHelper = this.helperList.find(h => h.operatorId === data.data.operatorId)
                if (existingHelper) return

                this.helperList.unshift(data.data)
                const sIndex = this.channels.findIndex(c => c.id === data.data.serviceSystemId)
                if (sIndex !== -1) {
                  if (this.userData.id === data.data.operatorId) {
                    this.channels[sIndex].helperStatus = data.data.status
                  }
                  this.channels.splice(sIndex, 1, {
                    ...this.channels[sIndex],
                    helperOperatorIds: [
                      ...(this.channels?.[sIndex]?.helperOperatorIds || []),
                      data.data.operatorId,
                    ],
                  })
                }
                this.activeChannel = this.channels[sIndex] || {
                  ...this.activeChannel,
                  helperOperatorIds: [
                    ...(this.activeChannel?.helperOperatorIds || []),
                    data.data.operatorId,
                  ],
                  helperStatus: data.data.status,
                }
              }
              break
            case 'statusChange':
              {
                const hIndex = this.helperList.findIndex(h => h.operatorId === data.data.operatorId)
                const sIndex = this.channels.findIndex(c => c.id === data.data.serviceSystemId)

                if (hIndex !== -1) {
                  this.helperList.splice(hIndex, 1, {
                    ...this.helperList[hIndex],
                    ...data.data,
                  })
                }

                if (sIndex !== -1 && this.userData.id === data.data.operatorId) {
                  this.channels[sIndex].helperStatus = data.data.status
                }
                this.activeChannel = this.channels[sIndex] || {
                  ...this.activeChannel,
                  helperOperatorIds: [
                    ...(this.activeChannel?.helperOperatorIds || []),
                    data.data.operatorId,
                  ],
                  helperStatus: data.data.status,
                }
              }
              break
            case 'removeHelper':
              {
                this.helperList = this.helperList.filter(h => h.operatorId !== data.data.operatorId)
                const sIndex = this.channels.findIndex(c => c.id === data.data.serviceSystemId)

                if (sIndex !== -1) {
                  if (this.userData.id === data.data.operatorId) {
                    this.channels[sIndex].helperStatus = data.data.status
                  }
                  this.channels.splice(sIndex, 1, {
                    ...this.channels[sIndex],
                    helperOperatorIds: this.channels?.[sIndex]?.helperOperatorIds.filter(id => id !== data.data.operatorId),
                  })
                }
                this.activeChannel = this.channels[sIndex] || {
                  ...this.activeChannel,
                  helperOperatorIds: this.activeChannel?.helperOperatorIds.filter(id => id !== data.data.operatorId),
                  helperStatus: data.data.status,
                }
              }
              break
            case 'replaceHelper':
              {
                const sIndex = this.channels.findIndex(c => c.id === data.data.serviceSystemId)
                const replacedId = data.data.replacedParentId
                const replacedIndex = this.helperList.findIndex(h => h.helperId === replacedId)

                if (sIndex !== -1 && replacedIndex !== -1) {
                  const oldOperatorId = this.helperList?.[replacedIndex]?.assignOperatorId || this.helperList?.[replacedIndex]?.operatorId
                  if (!this.channels[sIndex].helperOperatorIds) this.channels[sIndex].helperOperatorIds = []
                  // this.channels[sIndex].helperOperatorIds = this.channels?.[sIndex]?.helperOperatorIds.map(id => (id === oldOperatorId ? data.data.operatorId : id))
                  this.channels.splice(sIndex, 1, {
                    ...this.channels[sIndex],
                    helperOperatorIds: this.channels?.[sIndex]?.helperOperatorIds.map(id => (id === oldOperatorId ? data.data.operatorId : id)),
                  })
                  this.helperList[replacedIndex] = data.data
                  if (this.userData.id === data.data.operatorId) {
                    this.channels[sIndex].helperStatus = data.data.status
                  }
                }
                this.activeChannel = this.channels[sIndex] || {
                  ...this.activeChannel,
                  helperOperatorIds: this.activeChannel?.helperOperatorIds.filter(id => id !== data.data.operatorId),
                  helperStatus: data.data.status,
                }
              }
              break
            default:
              //
              break
          }
        }
      })
      this.socket.on('merchantId:services', data => {
        if (data?.data) {
          try {
            data.data = JSON.parse(data.data)

            // if (window.DEBUG) {
            // console.log('[merchantId:services]: ', data)
            // }

            // Check project access
            const projectCondition = this.appStore.allowedProjects.includes('ALL_PROJECT')
                || this.appStore.allowedProjects.includes(data.data?.projectId)

            // Check service category access
            const categoryCondition = this.appStore.allowedServiceCategories.includes(
              'ALL_CATEGORY',
            )
                || this.appStore.allowedServiceCategories.includes(
                  data.data?.serviceCategory?.code,
                )

            // Check service type access
            const typeCondition = this.appStore.allowedServiceTypes.includes('ALL_TYPE')
                || this.appStore.allowedServiceTypes.includes(
                  data.data?.serviceType?.code,
                )

            // Check service sub type access
            const subTypeCondition = this.appStore.allowedServiceSubTypes.includes('ALL_SUBTYPE')
                || this.appStore.allowedServiceSubTypes.includes(
                  data.data?.serviceSubType?.code,
                )

            // Early return if incoming service is outside current query filters
            if (data.actionType !== 'serviceStatusBadgeCount' && !Array.isArray(data.data)) {
              const query = {
                ...(this.$route?.query || {}),
                ...(this.channelFilter || {}),
                ...(store?.state?.app?.serviceSystemQuery || {}),
              }
              const service = data.data || {}
              let outOfRange = false

              // Status
              if (query.status && service.status !== query.status) outOfRange = true

              // Operator
              if (query.operator && service.assignedOperatorId !== query.operator) outOfRange = true

              // Department
              if (query.departmentId && service.departmentId !== query.departmentId) outOfRange = true

              // Project
              if (query.projectId && service.projectId !== query.projectId) outOfRange = true

              // Origin
              if (query.origin && service.origin !== query.origin) outOfRange = true

              // Category
              if (query.serviceCategory && service?.serviceCategory?.code !== query.serviceCategory) outOfRange = true

              // Type
              if (query.serviceType && service?.serviceType?.code !== query.serviceType) outOfRange = true

              // SubType
              if (query.serviceSubType) {
                const subTypes = (service?.serviceSubTypes || []).map(s => s.code)
                if (!subTypes.includes(query.serviceSubType)) outOfRange = true
              }

              // Date range (use updatedAt fallback to createdAt)
              const createdAt = service.createdAt
              if (createdAt) {
                if (query.startDate && new Date(createdAt) < new Date(query.startDate)) outOfRange = true
                if (query.endDate && new Date(createdAt) > new Date(query.endDate)) outOfRange = true
              } else if (query.startDate || query.endDate) {
                outOfRange = true
              }

              // Search
              if (query.search) {
                const keyword = String(query.search).trim().toLowerCase()
                if (keyword) {
                  const hay = [
                    service.shortenName,
                    service?.name?.en,
                    service?.name?.zh,
                    service?.name?.km,
                    service?.name?.vi,
                    service.id,
                  ]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase()
                  if (!hay.includes(keyword)) outOfRange = true
                }
              }

              if (outOfRange) {
                if (this.$route.query.status && this.$route.query.status !== service.status) {
                  const removeId = service.serviceSystemId || service.id
                  console.log('removeId: ', removeId)
                  const idx = this.channels.findIndex(c => c.id === removeId)
                  if (idx !== -1) {
                    console.log('idx: ', idx)
                    this.channels.splice(idx, 1)
                    this.total -= 1
                  }
                } else {
                  // update service
                  const index = this.channels.findIndex(c => c.id === service.serviceSystemId || c.id === service.id)
                  if (index !== -1) {
                    this.channels.splice(index, 1, {
                      ...this.channels[index],
                      ...service,
                    })
                  }
                }
                return
              }
            }

            if (!projectCondition || !categoryCondition || !typeCondition || !subTypeCondition) {
              return
            }

            if (data.actionType === 'serviceStatusBadgeCount') {
              const userId = this.userData.id
              const statuses = data.data
              const serviceCategories = this.appStore.serviceCategories || []

              const badgeCount = {
                all: 0,
                pendingPrice: 0,
                pendingDispatch: 0,
              }

              serviceCategories.forEach(category => {
                badgeCount[category.code] = {
                  all: 0,
                  pendingPrice: 0,
                  pendingDispatch: 0,
                }
              })

              statuses.forEach(status => {
                const filteredServices = status.services.filter(
                  service => !service.assignedOperatorId
                      || service.assignedOperatorId === userId,
                )

                badgeCount[status.status] = filteredServices.length
                badgeCount.all += filteredServices.length

                filteredServices.forEach(service => {
                  const categoryCode = service.serviceCategory?.code
                  if (categoryCode && badgeCount[categoryCode]) {
                    badgeCount[categoryCode][status.status] = (badgeCount[categoryCode][status.status] || 0) + 1
                    badgeCount[categoryCode].all += 1
                  }
                })
              })

              this.appStore.setServiceBadgeCount(badgeCount)
              return
            }

            if (Array.isArray(data.data)) {
              data.data.forEach(item => {
                const index = this.channels.findIndex(c => c.id === item.id)
                if (index !== -1) {
                  this.channels[index] = item
                }

                if (this.activeChannel.id === item.id) {
                  this.activeChannel = item
                }
              })
              return
            }

            ServiceSystemRepository.getUnseenCount().then(response => {
              const unseenData = response?.data?.data
              this.unseenServiceTicket(unseenData.unseenCount)
            })

            const index = this.channels.findIndex(
              c => c.id === data.data.serviceSystemId,
            )

            // Handle new service creation
            if (
              (index === -1
                && data.actionType !== 'listServiceUpdate'
                && (!this.$route.query.status
                  || data.data.status === this.$route.query.status)
              ) || (index === -1 && !this.$route.query.status)
            ) {
              // Remove last item if we're at page limit
              if (this.channels.length >= 10) {
                this.channels.pop()
              }
              this.channels.unshift(data.data)
              this.total += 1 // Increment total count
              this.$nextTick(() => {
                this.scrollToBottom()
              })
              return
            }

            // Handle service removal/status change
            if (
              this.$route.query.status
                && data.data.status !== this.$route.query.status
                && index !== -1
            ) {
              this.channels.splice(index, 1)
              // this.total -= 1 // Decrement total count
            } else if (data.data.serviceSystemId) {
              if (
                index === -1
                  && data.data.status === this.$route.query.status
              ) {
                if (this.channels.length >= 10) {
                  this.channels.pop()
                }
                this.channels.unshift(data.data)
                this.total += 1 // Increment total count
              } else if (index !== -1 && !this.$route.query.status) {
                this.channels.splice(index, 1)
                const idx = this.channels.findIndex(
                  c => c.id === data.data.serviceSystemId,
                )
                if (idx === -1) {
                  if (this.channels.length >= 10) {
                    this.channels.pop()
                  }
                  this.channels.unshift(data.data)
                }
              }
            }

            // Handle active service updates
            if (this.activeChannel.id === data.data.serviceSystemId) {
              this.loadServiceDetail(data.data.serviceSystemId, () => {
                // this.markSeenService()
              })
            }

            // Update existing service in list
            const newIndex = this.channels.findIndex(
              c => c.id === data.data.serviceSystemId,
            )
            if (newIndex !== -1) {
              this.channels.splice(newIndex, 1, {
                ...this.channels[newIndex],
                ...data.data,
              })
            }
          } catch (error) {
            console.warn('[merchantId:services]: Error: ', error, data.data)
          }
        }
      })
      this.socket.on('merchantId:servicesId', data => {
        if (data?.data) {
          try {
            data.data = JSON.parse(data.data)

            // if (window.DEBUG) {
            // console.log('[merchantId:servicesId]: ', data)
            // }

            const idx = this.messages.findIndex(m => m.id === data.data.id)
            if (idx !== -1) return

            // Push new service system log to service tracking list
            if (this.activeChannel.id === data.data.serviceSystemId) {
              this.messages.push(data.data)
              this.$nextTick(() => {
                this.scrollToBottom()
              })
            }
          } catch (error) {
            console.warn(
              '[merchantId:servicesId]: Error: ',
              error,
              data.data,
            )
          }
        }
      })
    },

    synceChannel(data) {
      const channel = data.data
      if (
        this.channelFilter.state
          && channel.state != this.channelFilter.state
      ) {
        return
      }
      if (
        this.channelFilter.search
          && !this.isSearchingMessage
          && channel.name.indexOf(this.channelFilter.search) == -1
      ) {
        return
      }
      if (
        this.isSearchingMessage
          && channel.latestMessage?.message.indexOf(this.channelFilter.search)
            == -1
      ) {
        return
      }
      if (
        this.channelFilter.operator
          && channel.operatorAssigned != this.channelFilter.operator
      ) {
        return
      }
      if (this.channelFilter.type == 'unassign' && channel.operatorAssigned) {
        return
      }

      return true
    },

    removeChannel(index) {
      if (index != -1) {
        this.channels.splice(index, 1)
        return true
      }
    },

    toUtc(dateStr, hours = 0) {
      const date = new Date(dateStr)
      if (hours > 0) {
        date.setHours(date.getHours() + hours)
      }
      const utcDate = new Date(
        Date.UTC(
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
          date.getHours(),
          date.getMinutes(),
          date.getSeconds(),
        ),
      )
      return utcDate
    },

    addHours(dateStr, hours = 0) {
      const date = new Date(dateStr)
      if (hours > 0) {
        date.setHours(date.getHours() + hours)
      }
      return date
    },

    filter(query) {
      let type = ''
      let operator = null

      delete this.channelFilter.priority

      if (query.type === 'assignToMe') {
        operator = this.userData.id
      } else if (query.type === 'unassign') {
        type = 'unassign'
      } else if (query.operator) {
        operator = query.operator
      } else if (query.type === 'priority') {
        this.channelFilter.priority = 1
      }

      this.channelFilter.operator = operator
      this.channelFilter.type = type
      this.channelFilter.startDate = query.startDate
      this.channelFilter.endDate = query.endDate
      this.channelFilter.status = query.status
      this.channelFilter.departmentId = query.departmentId

      if (query.type === 'unassign') {
        this.channelFilter.status = 'unassign'
      }

      this.channels = []
      this.total = 0
      this.channelPage = 1
      this.identifier += 1

      // Load service system if ID is present
      if (query.serviceSystemId && this.$route.query.from == 'chat') {
        this.findServiceSystem()
      }
    },
    async reloadChannel() {
      const date = this.channels[0] ? this.channels[0].updatedAt : ''
      if (date) {
        ChatChannelRepository.latest({
          order: 'updatedAt',
          sort: 'ASC',
          date,
          ...this.channelFilter,
        })
          .then(response => {
            const data = response?.data?.data?.list
            if (data) {
              data.forEach(element => {
                const index = this.channels.findIndex(
                  channel => channel.channel === element.channel,
                )
                if (index !== -1) {
                  this.channels.splice(index, 1)
                }

                this.channels.unshift(element)

                if (this.activeChannel.channel === element.channel) {
                  this.activeChannel = {
                    ...element,
                  }
                }
              })
            }
          })
          .catch(() => {})
      } else {
        this.listChannel()
      }
    },
    unseenServiceTicket(value) {
      // this.serviceSidebar[0].tag = value > 99 ? '99+' : value
      // this.serviceSidebar = [...this.serviceSidebar]
    },
    async listChannel(query) {
      const { reset } = query || {}
      if (this.appStore.serviceCategories.length === 0) {
        this.channels = []
        return
      }

      // Check if coming from chat with serviceSystemId
      if (!reset && this.$route.query.from === 'chat'
          && this.$route.query.serviceSystemId
      ) {
        this.findServiceSystem()
        return
      }
      if (!this.userData.isOffline) {
        if (this.isAutoAssignOperator) {
          this.channelFilter.isAuto = true
        }
      }

      this.loading = true
      const params = {
        limit: this.channelPerPage,
        page: this.channelPage,
        order: 'updatedAt',
        ...this.channelFilter,
        ...query,
        ...this.$route.query,
        ...store.state.app.serviceSystemQuery,
      }
      if (params.origin === 'telegram') {
        params.subOrigin = 'telegram'
        params.origin = 'external'
      } else if(params.origin === 'application') {
        params.subOrigin = 'application'
        params.origin = 'external'
      } else {
        delete params.subOrigin
      }
      ServiceSystemRepository.index(params)
        .then(response => {
          // this.channelPage += 1

          const data = response?.data?.data
          if (data) {
            this.channels = data.list
            this.total = data.total
            if (this.$route.query.channel) {
              const { channel } = this.$route.query
              const selectedExist = this.channels.find(
                val => val.id == channel,
              )

              if (!selectedExist && this.$route.query.filter != '') {
                this.activeChannel.channel = null // force no slected channel
              }
            }

            // if ($state && data.list == 0) {
            //   $state.complete()
            // }
          }
        })
        .catch()
        .then(() => {
          // if ($state) {
          //   $state.loaded()
          // }
        })
        .finally(() => {
          this.loading = false
        })
    },
    async findServiceSystem($state = null, find = false) {
      ServiceSystemRepository.index({
        id: this.$route.query.serviceSystemId,
        status: this.$route.query.status,
        limit: 1, // We only need one record
        page: 1,
      })
        .then(response => {
          const data = response?.data?.data
          if (data?.list?.length > 0) {
            const serviceSystem = data.list[0]
            this.channels = [serviceSystem]
            this.openChannel(serviceSystem)
          }
          if ($state) {
            $state.complete()
          }
        })
        .catch()
        .then(() => {
          if ($state) {
            $state.loaded()
          }
        })
        .finally(() => {
          if ($state) {
            $state.loaded()
          }
        })
    },
    reloadMessage() {
      const date = this.messages[this.messages.length - 1]
        ? this.messages[this.messages.length - 1]?.createdAt
        : ''
      ChatMessageRepository.latest({
        channel: this.activeChannel.channel,
        date,
      })
        .then(response => {
          const data = response?.data?.data
          if (data) {
            data.reverse()
            data.forEach(element => {
              this.messages.push(element)
            })
            this.$nextTick(() => {
              this.scrollToBottom()
            })
          }
        })
        .catch(() => {})
    },
    closeChannel() {
      this.activeChannel = {}
    },
    keyCloseConversation(event) {
      if (!this.isModalOpen) {
        if (
          this.activeChannel.state === this.conversationStatus.open
            && !this.activeChannel.blockAt
            && this.activeChannel.operatorAssigned === this.userData.id
        ) {
          if (event.ctrlKey && event.key === 'F2') {
            this.confirmResolve(this.conversationStatus.close)
          }
        }
      }
    },
    handleScrollMessage(e) {
      const el = e.target
      const distance = el?.scrollHeight - (el?.scrollTop + el?.offsetHeight)
      if (distance <= 100) {
        this.hidePullDown = true
      } else if (this.hidePullDown) {
        this.hidePullDown = false
      }
    },
    fetchLatest() {
      this.scrollToBottom('smooth')
      // Proceed when infinite scroll not yet done.
      if (this.$refs.infiniteLoading_next.status !== 2) {
        // Needed to complete next scroll. Prevent unstop fetching.
        // this.$refs.infiniteLoading_next.stateChanger.complete()
        // Fetch latest messages.
        this.listMessage(null, null, true, true)
      }
    },
    listMessage_prev() {
      if (this.isDateTimeFilter) {
        // this.$refs.infiniteLoading_prev.stateChanger.complete()
        return
      }
      this.listMessage(
          // $state,
          this.messages[0]?.createdAt,
      )
    },
    listMessage_next() {
      this.listMessage(
          // $state,
          this.messages[this.messages.length - 1]?.createdAt,
          false,
      )
    },
    listMessage(
      // $state = null,
      date = null,
      reverse = true,
      fetchLatest = false,
      channel = null,
    ) {
      // For topic summary referent
      if (
        this.$route.query.channelId
          && this.$route.query.from === 'topic-summary'
      ) {
        if (this.$route.query.msgStartDate) {
          this.query.startDate = this.$route.query.msgStartDate
          this.search()
          // if ($state) {
          //   $state.complete()
          // }
          return
        }
      }
      const query = {}

      if (date) {
        if (reverse) {
          query.lte = date
        } else {
          query.gte = date
        }
      }

      ServiceSystemRepository.logs(
        {
          channel: channel || this.activeChannel.channel,
          offset: 0, // always 0
          ...query,
          limit: 1000,
        },
        this.activeChannel.id,
      )
        .then(response => {
          const data = response?.data?.data.list

          if (data) {
            let isScrollBottom = true
            if (fetchLatest) {
              this.messages = []
              // Needed to reset prev scroll
              // this.$refs.infiniteLoading_prev.stateChanger.reset()
            } else {
              isScrollBottom = this.messages.length === 0
            }
            this.$nextTick(() => {
              data.forEach(element => {
                if (element.serviceSystemId === this.activeChannel.id) {
                  const isExist = this.messages.findIndex(
                    message => message.id === element.id,
                  )
                  if (isExist === -1) {
                    if (reverse) {
                      this.messages.unshift({
                        ...element,
                        messageType: 4,
                      })
                    } else {
                      this.messages.push({
                        ...element,
                        messageType: 4,
                      })
                    }
                  }
                }
              })
            })

            // if ($state && data.length < this.messageLimit) {
            // $state.complete()
            // }

            if (isScrollBottom) {
              this.$nextTick(() => {
                setTimeout(() => {
                  this.scrollToBottom()
                }, 100)
              })
            }
          }
        })
        .catch()
        .then(() => {
          // if ($state) {
          //   $state.loaded()
          // }
        })
    },
    filterMessageByDateTime(date) {
      if (date.startDate === '' && date.endDate === '') return
      const query = {}
      if (date.startDate) {
        query.gte = date.startDate
        if (!date.endDate) {
          const today = new Date()
          today.setHours(23, 59, 59, 999) // Set to end of day
          query.lte = today.toISOString()
        }
      }

      if (date.endDate) {
        const endDate = new Date(date.endDate).setSeconds(59)
        query.lte = endDate
        if (!date.startDate) {
          this.listMessage(null, endDate, true, true)
          return
        }
      }

      ChatMessageRepository.index({
        channel: this.activeChannel.channel,
        limit: this.messageLimit,
        offset: this.isDateTimeFilter ? this.filterOffset : 0,
        ...query,
      })
        .then(response => {
          const data = response?.data?.data

          if (data) {
            if (!this.isDateTimeFilter) {
              this.messages = []
            }
            this.$nextTick(() => {
              data.forEach(element => {
                if (element.chatChannelId === this.activeChannel.id) {
                  const isExist = this.messages.findIndex(
                    message => message.ref === element.ref,
                  )
                  if (isExist === -1) {
                    this.messages.push(element)
                  }
                }
              })
            })
            if (data.length >= this.messageLimit) {
              this.filterMessageByDateTime(date)
            }
          }
        })
        .catch()
        .then(() => {
          this.isDateTimeFilter = true
          setTimeout(() => {
            this.scrollToTop()
            this.filterOffset += this.messageLimit
          }, 100)
        })
    },
    loadServiceDetail(id, cb) {
      ServiceSystemRepository.show(id).then(response => {
        const data = response?.data?.data
        if (data && this.activeChannel.id === data.id) {
          this.activeChannel = {
            ...this.activeChannel,
            ...data,
          }
          const index = this.channels.findIndex(
            channel => channel.id === data.id,
          )
          if (index !== -1) {
            this.channels[index] = this.activeChannel
          }
          if (cb && typeof cb === 'function') {
            cb()
          }
        }
      })
    },
    markSeenService() {
      ServiceSystemRepository.markSeenServiceTicket(
        this.activeChannel.id,
      ).then(() => {
        this.activeChannel.badgeCount = 0
        const index = this.channels.findIndex(
          channel => channel.id === this.activeChannel.id,
        )
        if (index !== -1) {
          this.channels[index].badgeCount = 0
        }
        ServiceSystemRepository.getUnseenCount().then(response => {
          const data = response?.data?.data
          this.unseenServiceTicket(data.unseenCount)
        })
      })
    },
    openChannel(channel, type = 'click') {
      if (this.$route.query.serviceSystemId === channel.id
        && this.$route.query.serviceSystemId === this.activeChannel.id
      && this.appStore.serviceContentVisible) {
        return
      }

      this.activeTab = 0
      this.nestedActiveTab = 0
      this.isTyping = false
      this.activeChannel = channel

      // this.markSeenService()
      this.loadServiceDetail(channel.id)

      if (this.activeChannel.operatorAssigned === this.userData.id) {
        this.activeChannel.badgeCount = 0
      }
      this.mqShallShowLeftSidebar = false
      this.messages = []

      this.joinChannel(this.activeChannel)

      this.appStore.setServiceContentVisible(true)

      // If coming from chat with a specific date
      if (
        this.$route.query.from === 'chat'
          && this.$route.query.msgStartDate
      ) {
        this.query.startDate = this.$route.query.msgStartDate
        this.search()
        return
      }

      // Default behavior for loading messages
      this.listMessage()
    },
    async sendTextMessage(message, translate) {
      this.isSendingMsg = true
      if (!message) {
        return
      }
      if (this.isDateTimeFilter || this.query.endDate) {
        this.resetDate()
      }

      this.$refs.chatInput.typing(false)

      const data = {
        ref: uuidv4(),
        createdAt: moment(),
        message,
        messageType: this.messageType.text,
        token: this.activeChannel.token,
      }

      await this.sendMessage(data, translate)
      this.$nextTick(() => {
        setTimeout(() => {
          this.isSendingMsg = false
        }, 100)
      })
    },
    sendAttachmentMessage(message) {
      if (!message.file) {
        return
      }
      this.$refs.chatInput.typing(false)

      const data = {
        ref: uuidv4(),
        createdAt: moment(),
        file: message.file,
        message: message.fileName,
        mediaUrl: message.fileUrl,
        messageType: this.messageType.attachment,
        token: this.activeChannel.token,
      }

      this.sendMessage(data)
    },
    retrySendMessage(index) {
      if (this.activeChannel.state != this.conversationStatus.open) {
        return
      }
      const message = this.messages[index]
      this.messages.splice(index, 1)
      this.sendMessage(message)
    },
    manualTranslate(index) {
      const message = this.messages[index]
      const target = this.$i18n.locale
      TranslateRepository.translate({ text: message.message, target }).then(
        response => {
          this.messages[index].translatedMessage = response?.data?.data?.translatedMessage
          this.messages[index].translatedLanguage = response?.data?.data?.translatedLanguage
          this.messages[index].detectedLanguage = response?.data?.data?.detectedLanguage

          this.chatBoxKey += 1
        },
      )
    },
    async uploadImage(message) {
      const formData = new FormData()
      formData.append('image', message.file)

      const response = await Client.post(
        `${Config.basePath}/upload/file`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: function (progressEvent) {
            const index = this.messages.findIndex(
              element => element.ref == message.ref,
            )
            if (index != -1) {
              this.messages[index].uploadPercentage = parseInt(
                Math.round((progressEvent.loaded / progressEvent.total) * 100),
              )
            }
          }.bind(this),
        },
        {
          timeout: 10000000,
        },
      )

      const mediaId = response?.data?.data?.mediaId
      if (mediaId) {
        return mediaId
      }
      const index = this.messages.findIndex(
        element => element.ref == message.ref,
      )
      if (index != -1) {
        this.messages[index].sentStatus = messageStatus.fail
      }
      return false
    },
    async sendMessage(message, translate) {
      const newMessage = {
        ...message,
        translatedMessage: translate?.translatedMessage,
        translatedLanguage: translate?.translatedLanguage,
        detectedLanguage: translate?.detectedLanguage,
        uploadPercentage: 0,
        participant: {
          participantType: 'merchant',
          id: this.userData.id,
        },
        profileUrl: this.profilePicture,
        sentStatus: this.messageStatus.sending,
      }

      this.messages.push(newMessage)

      this.$nextTick(() => {
        if (this.$refs.infiniteLoading_next.status == 2) {
          this.scrollToBottom('smooth')
        }
      })

      if (message.file) {
        const mediaId = await this.uploadImage(message)
        if (!mediaId) {
          return
        }

        message.mediaId = mediaId
      }

      ChatMessageRepository.create({
        message: message.message,
        messageType: message.messageType,
        mediaId: message.mediaId,
        ref: message.ref,
        token: message.token || this.activeChannel.token,
      })
        .then(response => {
          const data = response?.data?.data
          if (data) {
            const index = this.messages.findIndex(
              element => element.ref == message.ref,
            )

            if (index != -1) {
              this.messages[index].id = data.id
              this.messages[index].createdAt = data?.createdAt
              this.messages[index].sentStatus = this.messageStatus.sent
              this.messages[index].mediaUrl = data.mediaUrl
            }

            // if it's jumping
            if (this.$refs.infiniteLoading_next.status != 2) {
              this.fetchLatest(newMessage)
            }
          }
        })
        .catch(error => {
          const index = this.messages.findIndex(
            element => element.ref == message.ref,
          )
          if (index != -1) {
            this.messages[index].sentStatus = this.messageStatus.fail
          }
        })
    },
    getChannelStateVariant(state) {
      if (state === this.conversationStatus.pending) {
        return 'warning'
      }
      if (state === this.conversationStatus.processing) {
        return {
          background: '#007bff',
          color: '#fff',
        }
      }
      if (state === this.conversationStatus.cancel) {
        return 'danger'
      }
      if (state === this.conversationStatus.hold) {
        return 'primary'
      }
      if (state === this.conversationStatus.completed) {
        return 'success'
      }

      return 'secondary'
    },
    assignOperator() {
      this.$refs.assignOperatorModal.show(
        this.activeChannel.token,
        !!this.activeChannel.operatorAssigned,
      )
    },
    transferToOtherMerchant() {
      const channelToken = this.activeChannel.token
      PartnersShipRepository.show()
        .then(response => {
          const { data } = response.data
          const partnerShip = [...data.list]
          if (partnerShip && partnerShip.length >= 1) {
            this.$refs.assignMerchantModal.show(
              channelToken,
              !!this.activeChannel.operatorAssigned,
            )
          } else {
            this.$swal.fire({
              text: this.$t('alert.pleaseAskAdminToBindAccount'),
              icon: 'warning',
              confirmButtonText: this.$t('button.close'),
            })
          }
        })
        .catch()
    },
    confirmResolve(state) {
      this.isModalOpen = true
      this.$bvModal
        .msgBoxConfirm(this.$t('alert.areYouSureToResolve'), {
          title: this.$t('alert.areYouSure'),
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'primary',
          okTitle: this.$t('chat.markAsResolve'),
          cancelTitle: this.$t('button.cancel'),
          cancelVariant: 'outline-secondary',
          hideHeaderClose: false,
          centered: true,
        })
        .then(value => {
          if (value) {
            this.updateStatus(state)
            if (this.isDateTimeFilter || this.query.endDate) {
              this.resetDate()
            }
          } else {
            this.isModalOpen = false
          }
        })
    },
    confirmOpen(state) {
      this.$bvModal
        .msgBoxConfirm(this.$t('alert.areYouSureToOpen'), {
          title: this.$t('alert.areYouSure'),
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'primary',
          okTitle: this.$t('button.openConversation'),
          cancelTitle: this.$t('button.cancel'),
          cancelVariant: 'outline-secondary',
          hideHeaderClose: false,
          centered: true,
        })
        .then(value => {
          if (value) {
            if (this.isDateTimeFilter || this.query.endDate) {
              this.resetDate()
            }
            this.fetchLatest()
            this.updateStatus(state)
          }
        })
    },
    confirmBan() {
      this.$bvModal
        .msgBoxConfirm(this.$t('alert.areYouSureToBan'), {
          title: this.$t('alert.areYouSure'),
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: this.$t('chat.banThisCustomer'),
          cancelTitle: this.$t('button.cancel'),
          cancelVariant: 'outline-secondary',
          hideHeaderClose: false,
          centered: true,
        })
        .then(value => {
          if (value) {
            this.block()
            if (this.isDateTimeFilter || this.query.endDate) {
              this.resetDate()
            }
          }
        })
    },
    block() {
      const data = {
        token: this.activeChannel.token,
      }
      ChatChannelRepository.block(data)
        .then(response => {
          const data = response?.data?.data
          if (data) {
            this.activeChannel = {
              ...data,
            }
          }
        })
        .then(error => {})
    },
    confirmUnban() {
      this.$bvModal
        .msgBoxConfirm(this.$t('alert.areYouSureToUnban'), {
          title: this.$t('alert.areYouSure'),
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'primary',
          okTitle: this.$t('chat.unbanThisCustomer'),
          cancelTitle: this.$t('button.cancel'),
          cancelVariant: 'outline-secondary',
          hideHeaderClose: false,
          centered: true,
        })
        .then(value => {
          if (value) {
            this.unblock()
            if (this.isDateTimeFilter || this.query.endDate) {
              this.resetDate()
            }
          }
        })
    },
    unblock() {
      const data = {
        token: this.activeChannel.token,
      }
      ChatChannelRepository.unblock(data)
        .then(response => {
          const data = response?.data?.data
          if (data) {
            this.activeChannel = {
              ...data,
            }
          }
        })
        .then(error => {})
    },
    updateStatus(state) {
      // at stop translate at here
      const data = {
        state,
        token: this.activeChannel.token,
      }
      ServiceSystemRepository.updateStatus(data)
        .then(response => {
          const data = response?.data?.data
          if (data && !this.isSearchingMessage) {
            this.activeChannel = {
              ...data,
            }
          }
        })
        .then(error => {
          this.isModalOpen = false
        })
    },
    submitReadMessage(token, messageId, readAt) {
      if (!token || !messageId || !readAt) {
        return
      }

      ChatMessageRepository.seen({
        token,
        messageId,
        readAt,
      })
        .then(response => {})
        .catch(err => {})
    },
    autoAssignToOperator() {
      if (this.activeChannel.operatorAssigned) {
        return
      }

      ChatChannelRepository.assign({
        token: this.activeChannel.token,
        userId: this.userData.id,
      })
        .then(response => {
          this.hide()
          this.$emit('success')
        })
        .catch(error => {
          if (error.response?.status == 422) {
            this.$refs.assignOperatorForm.setErrors(
                error.response?.data?.validate,
            )
          }
        })
        .then(() => {
          this.loading = false
        })
    },
    onBodyClick() {
      if (this.$refs.chatInput) {
        this.$refs.chatInput.closeEmoji()
      }
    },
    createDebounce() {
      let timeout = null
      return function (fnc, delayMs) {
        clearTimeout(timeout)
        timeout = setTimeout(() => {
          fnc()
        }, delayMs || 500)
      }
    },
    resizeChatBody() {
      this.hidePullDown = true
      const messageBodyEl = document.getElementsByClassName('messages-body')
      const messageNobodyEl = document.getElementsByClassName(
        'message-body-no-chat-input',
      )
      const chatFooterEl = document.getElementsByClassName('chat-footer')
      const userChat = document.getElementsByClassName('user-chats')
      if (messageNobodyEl.length > 0 && chatFooterEl.length > 0) {
        const chatFooterHeight = chatFooterEl[0].offsetHeight
        messageNobodyEl[0].style.height = `calc(100% - ${chatFooterHeight}px)`
      } else if (messageNobodyEl.length > 0 && chatFooterEl.length === 0) {
        messageNobodyEl[0].style.maxHeight = `${messageNobodyEl[0]?.scrollHeight}px`
        messageNobodyEl[0].style.height = `${messageNobodyEl[0]?.scrollHeight}px`
      }
    },
    resetChatBodyResize() {
      const messageBodyEl = document.getElementsByClassName('messages-body')
      const chatFooterEl = document.getElementsByClassName('chat-footer')
      if (messageBodyEl.length > 0 && chatFooterEl.length > 0) {
        messageBodyEl[0].style.height = 'calc(100% - 65px)'
      }
    },
    toggleDialog() {
      this.dialogVisible = !this.dialogVisible
    },
    resetDate() {
      this.$router.replace({
        query: {
          ...this.$router.query,
          channelId: null,
          msgStartDate: null,
          from: null,
        },
      })

      this.listMessage(null, null, true, true)
      this.query.startDate = ''
      this.query.endDate = ''
      this.isDateTimeFilter = false
      this.filterOffset = 0
      this.dialogVisible = false
    },
    search() {
      this.isDateTimeFilter = false
      this.filterOffset = 0
      this.filterMessageByDateTime(this.query)
      this.dialogVisible = false
    },
    onResizeChatBox() {
      this.hidePullDown = true
      const chatFooterEl = document.getElementsByClassName('chat-footer')
      if (chatFooterEl.length > 0) {
        const chatFooterHeight = chatFooterEl[0].offsetHeight
        this.footerHeight = chatFooterHeight
      }
    },
    handleOpenOperator() {
      this.$refs.assignOperatorChatModal.show(this.activeChannel)
    },
    handleOpenStatus(type, title) {
      this.$refs.updateStatusChatModal.show(this.activeChannel, type, title)
    },
    handleChangeCompleteStatus() {
      this.$refs.updateCompleteStatusRefStandalone.show(
        this.activeChannel.id,
      )
    },
    handleChangeHoldStatus() {
      this.$refs.holdDurationRef.show(
        this.activeChannel.status,
        this.activeChannel.id,
      )
    },
    onAssignSuccess(data) {
      this.activeChannel.assignedOperator = data.data.data.assignedOperator
      this.activeChannel.assignedOperatorId = data.data.data.assignedOperatorId
    },
    hideCompleteStatusModal() {
      this.$refs.updateCompleteStatusRef.hide()
    },
    onUpdateStatusSuccess(data) {
      if (!this.isHelper) {
        this.activeChannel.status = data.data.data.status
      }

      this.hideCompleteStatusModal()
    },
    onCloseHoldModal() {
      this.$refs.updateStatusChatModal.onCloseHoldModal()
    },
    onUpdateCompleteStatusModal({ remark, remarkMediaIds, servicePrice }) {
      this.$refs.updateStatusChatModal.onUpdateCompleteStatusModal(
        remark,
        'pendingPrice',
        this.activeChannel,
        remarkMediaIds,
        servicePrice,
        this.isHelper,
      )
    },
    onUpdateCompleteStatus(data) {
      this.$refs.updateCompleteStatusRef.show(data.remark)
    },
    onUpdatePendingPrice() {
      this.$refs.pendingPriceModal.show()
    },
    handlePendingPriceSuccess(response) {
      if (response?.data?.data && !this.isHelper) {
        // Update active channel
        this.activeChannel = {
          ...this.activeChannel,
          servicePrice: response.data.data.servicePrice,
          servicePriceType: response.data.data.servicePriceType,
          servicePriceMin: response.data.data.servicePriceMin,
          servicePriceMax: response.data.data.servicePriceMax,
          servicePriceCurrency: response.data.data.servicePriceCurrency,
          status: response.data.data.status,
        }

        // Update status in channels list for ChatContact
        const channelIndex = this.channels.findIndex(
          channel => channel.id === this.activeChannel.id,
        )
        if (channelIndex !== -1) {
          this.channels[channelIndex] = {
            ...this.channels[channelIndex],
            servicePrice: response.data.data.servicePrice,
            servicePriceType: response.data.data.servicePriceType,
            servicePriceMin: response.data.data.servicePriceMin,
            servicePriceMax: response.data.data.servicePriceMax,
            servicePriceCurrency: response.data.data.servicePriceCurrency,
            status: response.data.data.status,
          }
        }
      }
      this.pendingPriceModalVisible = false
    },
    handleShowCompleteModal({ servicePrice, servicePriceCurrency }) {
      this.$refs.updateCompleteStatusRef.show()
      this.activeChannel.servicePrice = servicePrice
      this.activeChannel.servicePriceCurrency = servicePriceCurrency
    },
    onCloseCancelServiceModal() {
      this.showCancelServiceModal = false
      this.data.remark = ''
      this.data.duration = 0
      this.$refs.cancelServiceForm.reset()
      this.cancelServiceLoading = false
    },
    handleCancelService() {
      this.data.remark = ''
      this.data.duration = 0
      this.$refs.cancelServiceForm.reset()
      this.showCancelServiceModal = true
    },
    handleConfirmCancelService(duration, remark) {
      if (duration) this.data.duration = duration
      if (remark) this.data.remark = remark
      this.$refs.cancelServiceForm.validate().then(success => {
        if (success) {
          this.$refs.formConfirmation.confirm()
        }
      })
    },
    async onUpdateCancelService() {
      this.cancelServiceLoading = true
      try {
        await ServiceSystemRepository.updateStatus(
          {
            status: 'cancel',
            ...(this.data.duration && {
              noOfminuteHold: +this.data.duration,
            }),
            ...(this.data.remark && { remark: this.data.remark.trim() }),
          },
          this.activeChannel.id,
        )

        this.$toast({
          component: ToastificationAvatar,
          position: 'top-right',
          timeout: 10000,
          props: {
            title: this.$t('alert.service'),
            icon: 'CastIcon',
            variant: 'success',
            text: this.$t('alert.serviceUpdatedSuccessfully', {
              status: this.$t('status.cancel'),
            }),
          },
        })

        this.data.remark = ''
        this.data.duration = 0
        this.$refs.cancelServiceForm.reset()
        this.showCancelServiceModal = false
        this.cancelServiceLoading = false
      } catch (error) {
        this.cancelServiceLoading = false
        if (error.response?.status === 422) {
          this.$refs.cancelServiceForm.setErrors(
              error.response?.data?.validate,
          )
        }
      }
    },
  },
  setup() {
    // Scroll to Bottom ChatLog
    const refChatLogPS = ref(null)
    const appStore = useAppStore()
    const scrollToBottom = (behavior = 'instant', isTyping = false) => {
      const scrollEl = refChatLogPS.value?.$el || refChatLogPS.value
      const distant = scrollEl?.scrollHeight
          - (scrollEl?.scrollTop + scrollEl?.offsetHeight)

      if (scrollEl) {
        if (!isTyping) {
          scrollEl.scrollTo({ top: scrollEl?.scrollHeight, behavior })
        } else if (distant <= 50 && isTyping) {
          scrollEl.scrollTo({ top: scrollEl?.scrollHeight, behavior })
        }
      }
    }
    const scrollToTop = (behavior = 'instant') => {
      const scrollEl = refChatLogPS.value?.$el || refChatLogPS.value
      if (scrollEl) {
        scrollEl.scrollTo({ top: 0, behavior })
      }
    }

    // UI + SM Devices
    // Left Sidebar Responsiveness
    const { mqShallShowLeftSidebar, mqShallShowRightSidebar } = useResponsiveAppSidebarVisibility()
    const startConversation = () => {
      if (store.state.app.windowWidth < $themeBreakpoints.lg) {
        mqShallShowLeftSidebar.value = true
      }
    }

    return {
      refChatLogPS,
      scrollToBottom,
      languageEn,
      languageKm,
      languageZh,
      languageVi,
      localeCountries,
      getCountryByLanguageCode,
      // UI + SM Devices
      startConversation,
      mqShallShowLeftSidebar,
      mqShallShowRightSidebar,
      dateInputs: DateInputs,
      scrollToTop,
      appStore,
    }
  },
}
</script>

<style lang="scss">
  @import '~@core/scss/base/pages/app-chat.scss';
  @import '~@core/scss/base/pages/app-chat-list.scss';

  .detectLanguage-dropdown .dropdown-menu {
    max-height: 300px;
    overflow-y: auto;
  }

  .sticky-search {
    position: sticky;
    top: 0;
  }

  .flag {
    font-size: 18px;
  }

  .translateBox {
    color: #fff;
    padding: 5px;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: center;
    align-items: center;
    align-content: center;
  }

  .fixed-date-picker-dialog {
    position: fixed;
    height: 274px;
    top: 55px;
    left: -150px;
    width: 300px;
    z-index: 1000;
    background-color: #fff;
    border: 0px solid #ccc;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    z-index: 2;
  }

  .action-btn-group {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0 1rem;
  }

  .back-icon {
    font-size: 1rem;
    cursor: pointer;
    color: #4e4e4e;
    display: flex;
    gap: 0.5rem;
    align-items: center;
    font-weight: 500;
  }

  .display-none .active-chat {
    border: 1px solid #e5e5e5;
    border-radius: 10px;

    .chat-header {
      border-top-left-radius: 10px;
      border-top-right-radius: 10px;
    }
  }

  .btn-con {
    padding: 1rem 1rem 1rem 0;
  }

  .service-icon {
    padding: 2px;
  }

  .service-icon-2,
  .service-icon-3 {
    padding: 3px;
  }

  .pending {
    color: #ffc107;
  }

  .bg-pending {
    background-color: #ffc107;
  }

  .processing {
    color: rgb(0, 123, 255);
  }

  .bg-processing {
    background-color: rgb(0, 123, 255);
  }

  .cancel {
    color: #c0392b;
  }

  .pendingDispatch {
    color: #ffc107;
  }

  .bg-cancel {
    background-color: #c0392b;
  }

  // .unresolved {
  //   color: #c0392b;
  // }

  // .bg-unresolved {
  //   background-color: #c0392b;
  // }

  .unresolved {
    color: #ff8c00;
    /* Dark Amber */
  }

  .bg-unresolved {
    background-color: #ff8c00;
    /* Matching background */
  }

  .hold {
    color: #8d5185;
  }

  .bg-hold {
    background-color: #8d5185;
  }

  .completed {
    color: #27ae60;
  }

  .resolved {
    color: #7e7e7e;
  }

  .bg-completed {
    background-color: #27ae60;
  }

  .bg-assign {
    // make any color different from bg-*
    background-color: #97b834;
  }

  .bg-resolved {
    background-color: #7e7e7e;
  }

  .bg-handover {
    background-color: var(--primary);
  }

  .handover {
    color: var(--primary);
  }

  .rejected {
    color: #e74c3c;
  }

  .bg-rejected {
    background-color: #e74c3c;
  }

  .bg-accepted {
    background-color: #2ecc71;
  }

  .bg-pendingPrice {
    background-color: #f1c40f;
  }

  .accepted {
    color: #2ecc71;
  }

  .pendingPrice {
    color: #f1c40f;
  }

  .btn-group-actions {
    display: flex;
    gap: 0.5rem;

    .btn {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .btn-reject {
      display: flex;
      align-items: center;
      gap: 4px;
      background: #ff4141 !important;
      border-color: #ff4141 !important;
    }

    .btn-accept {
      display: flex;
      align-items: center;
      gap: 4px;
      background: #48b35e !important;
      border-color: #48b35e !important;
    }
  }
</style>
